/**
 * Tenants (CRM/person) domain API functions (Step 7.1.9).
 *
 * Tenant CRUD, notes, and events for the tenant person record.
 * NOT tenancy (contract) functions — those are in tenancies.js.
 * NOT tenant documents — those are in documents.js.
 * Extracted from adminData/index.js for team scalability.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  expectPaginatedItems,
  parseAdminErrorResponse,
  parseAdminErrorBodyText,
} from "./core";

/**
 * @param {{ skip?: number, limit?: number, q?: string }} [params]
 */
export function fetchAdminTenants(params = {}) {
  const sp = new URLSearchParams();
  if (params.skip != null) sp.set("skip", String(params.skip));
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.q != null && String(params.q).trim()) {
    sp.set("q", String(params.q).trim());
  }
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/tenants${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders() })
    .then((res) => {
      if (!res.ok) throw new Error("Tenants konnten nicht geladen werden.");
      return res.json();
    })
    .then((data) => expectPaginatedItems(data, "GET /api/admin/tenants"));
}

/**
 * GET /api/admin/tenants — FULL ``{items, total}`` envelope (C3).
 *
 * Same endpoint and parameters as ``fetchAdminTenants``; the difference is
 * that the envelope survives instead of being unwrapped to a bare array.
 * ``AsyncCombobox`` needs both halves — ``items`` to render the page and
 * ``total`` to decide whether "Mehr laden" belongs there. Reading
 * ``.items`` off an array yields ``undefined``, which the base renders as
 * an empty result set (the C2 regression this split exists to prevent).
 *
 * ``fetchAdminTenants`` keeps its array contract: several admin pages and
 * the task form consume it that way.
 *
 * ``without_tenancy: true`` narrows the list to tenants attached to no tenancy
 * at all — neither as a tenancy's primary tenant nor as a participant. It is
 * the only way to reach those people from a list: ``GET /api/admin/tenancies``
 * starts from ``select(Tenancy)`` and can never produce a row for one.
 */
export function fetchAdminTenantsPage({
  q,
  limit,
  offset,
  without_tenancy,
} = {}) {
  const sp = new URLSearchParams();
  if (offset != null) sp.set("skip", String(offset));
  if (limit != null) sp.set("limit", String(limit));
  if (q != null && String(q).trim()) sp.set("q", String(q).trim());
  // Sent ONLY when explicitly true. An omitted parameter and
  // `without_tenancy=false` mean the same thing to the endpoint, so leaving it
  // off keeps the URL of every existing caller byte-identical.
  if (without_tenancy === true) sp.set("without_tenancy", "true");
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/tenants${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders() }).then(async (res) => {
    if (!res.ok) throw new Error("Mieter konnten nicht geladen werden.");
    const data = await res.json();
    const items = expectPaginatedItems(data, "GET /api/admin/tenants");
    return {
      items,
      total: typeof data?.total === "number" ? data.total : items.length,
    };
  });
}

export function fetchAdminTenant(tenantId) {
  return fetch(
    `${API_BASE_URL}/api/admin/tenants/${encodeURIComponent(tenantId)}`,
    { headers: getApiHeaders() }
  ).then((res) => {
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error("Mieter konnte nicht geladen werden.");
    }
    return res.json();
  });
}

export async function createAdminTenant(body) {
  const res = await fetch(`${API_BASE_URL}/api/admin/tenants`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export async function updateAdminTenant(tenantId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenants/${encodeURIComponent(tenantId)}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export async function deleteAdminTenant(tenantId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenants/${encodeURIComponent(tenantId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  const text = await res.text();
  if (!res.ok) {
    let msg = parseAdminErrorBodyText(text).trim();
    if (!msg || msg === "Die Anfrage ist fehlgeschlagen.") {
      msg = `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`;
    }
    throw new Error(msg);
  }
  if (!text || !text.trim()) return { status: "ok" };
  try {
    return JSON.parse(text);
  } catch {
    return { status: "ok" };
  }
}

export function fetchAdminTenantNotes(tenantId) {
  return fetch(
    `${API_BASE_URL}/api/admin/tenants/${encodeURIComponent(tenantId)}/notes`,
    { headers: getApiHeaders() }
  ).then((res) => {
    if (!res.ok) {
      if (res.status === 404) return { items: [] };
      throw new Error("Notizen konnten nicht geladen werden.");
    }
    return res.json();
  });
}

export async function createAdminTenantNote(tenantId, content) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenants/${encodeURIComponent(tenantId)}/notes`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify({ content }),
    }
  );
  // Read body exactly once — avoids "body stream already read" if anything else touched the stream.
  const text = await res.text();
  if (!res.ok) {
    throw new Error(parseAdminErrorBodyText(text));
  }
  try {
    return text ? JSON.parse(text) : {};
  } catch (e) {
    console.warn("createAdminTenantNote: unexpected response body", e);
    throw new Error("Notiz konnte nicht gespeichert werden.");
  }
}

export function fetchAdminTenantEvents(tenantId) {
  return fetch(
    `${API_BASE_URL}/api/admin/tenants/${encodeURIComponent(tenantId)}/events`,
    { headers: getApiHeaders() }
  ).then((res) => {
    if (!res.ok) {
      if (res.status === 404) return { items: [] };
      throw new Error("Verlauf konnte nicht geladen werden.");
    }
    return res.json();
  });
}

/**
 * Read-only preview of merging source into survivor.
 * Returns 200 with { can_merge, conflicts, warnings, counts, planned_moves,
 * scalar_fields_to_fill, survivor, source } even when can_merge=false
 * (business conflicts live in the body, not in the HTTP status).
 * Only 404 (not found) / 422 (self-merge / bad body) throw.
 * @param {string} survivorId
 * @param {{ source_tenant_id: string, reason?: string|null }} payload
 */
export async function previewTenantMerge(survivorId, payload) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenants/${encodeURIComponent(
      survivorId
    )}/merge-preview`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

/**
 * Atomically merge source into survivor. Throws on 409 (business conflict),
 * 422 (self-merge / bad body) and 404 (not found) with the backend detail.
 * @param {string} survivorId
 * @param {{ source_tenant_id: string, reason?: string|null }} payload
 */
export async function executeTenantMerge(survivorId, payload) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenants/${encodeURIComponent(survivorId)}/merge`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}
